import React, {useState} from 'react';
import b_ from 'b_';
import {logError} from '@yandex-int/pythia-libs';

import {Domain} from 'types/domain';

import {CreateSegmentBox, UXBox, VideoBoxBox} from 'client/common/types';

import {Spin, Checkbox} from 'client/common/components/lego';
import CardPaymentForm from 'client/common/components/card-payment-form';

import {useBoxType} from 'client/common/hooks/box';
import {useModal} from 'client/common/hooks/modal';
import {
    getPaymentStatusKey,
    useCreatePaymentLink,
    usePayers,
    usePaymentStatus,
    usePayWithWallet,
    usePrice,
    useWallet
} from 'client/common/hooks/payment';
import {usePublishRevision} from 'client/common/hooks/revision';
import {useSurvey, useSurveyPackageInfo} from 'client/common/hooks/survey';
import {useIsLauncherRole, useIsSuperuser, useUserCountryId} from 'client/common/hooks/user';
import {
    BOX_TYPES_WITH_PACKAGE_PAY,
    usePackageBinding,
    useCanShowPackagePay
} from 'client/common/hooks/packages';

import Preview from 'client/common/components/preview/preview';

import {useSurveySlug} from 'client/common/contexts/slug';

import {openLink, reload} from 'client/common/utils/window';
import {tld} from 'client/common/utils/tld';
import {getIsWalletAvailable, getNeedToPay, getSurveyPriceLabels} from 'client/common/utils/price';
import {isPaymentAsIndividualAvailable} from 'client/common/utils/payment';
import {queryClient} from 'client/common/utils/react-query';

import I18N from 'assets/i18n/brandlift-project';

import {LaunchProps, PublicationProps} from './types';
import HelpBlock from '../help-block/help-block';
import {EmailBlock} from './email';
import {LaunchButtons} from './launch-buttons';

import './publication.css';
import {PayerCountry} from '../card-payment-form/types';

const i18n = I18N.common.publication;
const b = b_.with('publication');

export default function Publication(props: PublicationProps) {
    const {onClose, withPreview} = props;

    return (
        <div className={b()}>
            <Launch onClose={onClose} />
            {withPreview && <Preview className={b('preview')} onlyPanel withHeader={false} />}
        </div>
    );
}

interface PackagesPayView {
    packagesLeft: number;
}
function PackagesPayView({packagesLeft}: PackagesPayView) {
    // сколько пакетов списываем за опрос
    const packagePayCost = 1;

    return (
        <>
            <div className={b('description')}>{i18n.packages.desc}</div>
            {packagesLeft < 1 && (
                <h3 className={b('packages-header')}>{i18n.packages.noMorePackages}</h3>
            )}
            <div className={b('packages-row')}>
                <span className={b('packages-label')}>{i18n.packages.before}</span>
                <span className={b('packages-value')}>{packagesLeft}</span>
            </div>
            {packagesLeft > 0 && (
                <div className={b('packages-row')}>
                    <span className={b('packages-label')}>{i18n.packages.after}</span>
                    <span className={b('packages-value')}>{packagesLeft - packagePayCost}</span>
                </div>
            )}
        </>
    );
}

interface PackagePayLauncherViewProps {
    isFetching: boolean;
    isBinded: boolean;
}
function PackagePayLauncherView({isFetching, isBinded}: PackagePayLauncherViewProps) {
    return (
        <div className={b('packages')}>
            {isFetching ? (
                <Spin className={b('spin')} size='xs' view='default' progress />
            ) : (
                <div className={b('description')}>
                    {isBinded ? i18n.packages.binded : i18n.packages.unbinded}
                </div>
            )}
        </div>
    );
}

// eslint-disable-next-line complexity, max-statements
function Launch(props: LaunchProps) {
    const {onClose} = props;

    const [isWalletUsed, setIsWalletUsed] = useState(false);
    const [isEmailValid, setIsEmailValid] = useState(true);

    const slug = useSurveySlug();
    const survey = useSurvey(slug);
    const packageInfo = useSurveyPackageInfo(slug);
    const boxType = useBoxType(slug);
    const price = usePrice(slug);
    const paymentStatus = usePaymentStatus(slug);
    const isSuperuser = useIsSuperuser();
    const payers = usePayers();
    const wallet = useWallet();

    const publishRevision = usePublishRevision();
    const createPaymentLink = useCreatePaymentLink(slug);
    const payWithWallet = usePayWithWallet(slug);

    const [showModal, hideModal] = useModal();

    const userCountryId = useUserCountryId();

    const isLauncherRole = useIsLauncherRole();
    const packageBinding = usePackageBinding({boxType, slug});
    const canShowPackagePay = useCanShowPackagePay(slug);

    if (
        survey.status !== 'success' ||
        price.status !== 'success' ||
        price.isFetching ||
        paymentStatus.status !== 'success' ||
        paymentStatus.isFetching ||
        wallet.status !== 'success'
    ) {
        return (
            <div className={b('launch')}>
                <h1 className={b('title')}>{i18n.title.launch}</h1>
                <Spin className={b('spin')} size='s' view='default' progress />
            </div>
        );
    }

    const handlePayAsEntity = async () => {
        const redirect = await createPaymentLink();

        if (redirect) {
            openLink(redirect);
        }
    };

    const handlePaymentFormFinish = () => {
        hideModal();
        reload();
    };

    const handlePayAsIndividual = () => {
        showModal(
            <CardPaymentForm
                createPaymentLink={createPaymentLink}
                onError={err => logError({message: 'Error when paying as individual', err})}
                onFinish={handlePaymentFormFinish}
                payers={payers.data}
                userCountry={String(userCountryId) as PayerCountry}
            />,
            {withCross: true}
        );
    };

    const handlePayWithPackage = async () => {
        await queryClient.refetchQueries(getPaymentStatusKey(slug));
    };

    const needToPay = getNeedToPay(price.data, paymentStatus.data);
    const isWalletAvailable = getIsWalletAvailable(price.data, paymentStatus.data, wallet.data);

    const packagesLeft = packageInfo?.data?.count ?? 0;

    const {priceLabel, vatHint} = getSurveyPriceLabels(price.data);

    const canPayAsIndividual = isPaymentAsIndividualAvailable(userCountryId);

    const boxEmail = (survey.data?.box as VideoBoxBox | CreateSegmentBox | UXBox)?.context.email;

    return (
        <div className={b('launch')}>
            <h2 className={b('title')}>
                {BOX_TYPES_WITH_PACKAGE_PAY.includes(boxType!)
                    ? i18n.title.creatives
                    : i18n.title.launch}
            </h2>
            {canShowPackagePay && isLauncherRole && (
                <PackagePayLauncherView
                    isBinded={packageBinding.isBinded}
                    isFetching={packageBinding.isFetching}
                />
            )}
            {canShowPackagePay && !isLauncherRole && (
                <PackagesPayView packagesLeft={packagesLeft} />
            )}
            {!canShowPackagePay && (
                <div className={b('blocks')}>
                    <div className={b('price-block')}>
                        {needToPay ? (
                            <div className={b('price')}>
                                <span className={b('price-label')}>{priceLabel}</span>
                                {vatHint && (
                                    <span className={b('vat-label')}>{` / ${vatHint}`}</span>
                                )}
                            </div>
                        ) : (
                            <div className={b('price-label')}>{i18n.info.surveyIsPayed}</div>
                        )}
                        {
                            <span className={b('offer')}>
                                {i18n.offer}
                                <a
                                    href={i18n.offerURL.replace(
                                        '%s',
                                        [Domain.Com, Domain.ComTr, Domain.Ua].includes(tld)
                                            ? Domain.Ru
                                            : tld
                                    )}
                                    className={b('link')}
                                    rel='noopener noreferrer'
                                    target='_blank'
                                >
                                    {i18n.offerLink}
                                </a>
                            </span>
                        }
                    </div>
                    <div className={b('side-block')}>
                        <EmailBlock
                            slug={slug}
                            isBox={boxType !== undefined}
                            initialEmail={boxEmail}
                            onValidityChange={(isValid: boolean) => setIsEmailValid(isValid)}
                        />
                        <br />
                        {isWalletAvailable && (
                            <div className={b('wallet-block')}>
                                <div className={b('wallet-group')}>
                                    <Checkbox
                                        checked={isWalletUsed}
                                        className={b('wallet-checkbox')}
                                        label={i18n.payWithWallet}
                                        onChange={e => setIsWalletUsed(e.target.checked)}
                                        size='m'
                                        view='default'
                                    />
                                    <HelpBlock>{i18n.hintContent}</HelpBlock>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <LaunchButtons
                disabled={!isEmailValid}
                isSuperuser={isSuperuser}
                isWalletUsed={isWalletUsed}
                isPackageUsed={canShowPackagePay}
                needToPay={needToPay}
                canPayAsIndividual={canPayAsIndividual}
                onClose={onClose}
                onPayAsEntity={handlePayAsEntity}
                onPayAsIndividual={handlePayAsIndividual}
                onPayWithWallet={payWithWallet}
                onPayWithPackage={handlePayWithPackage}
                onPublishRevision={publishRevision}
                launchBtnText={canShowPackagePay ? i18n.launchTest : i18n.launchSurvey}
            />
        </div>
    );
}
